import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Company,
  RestrictionZone,
  UserZoneBookingObject
} from "../../Booking-Form/typings/booking.types";
import {
  CapacityObject,
  EquipmentFilter,
  MapMarkerLocation,
  ReportEntities,
  ReportFilters,
  TimeframeString
} from "../typings/reports.types";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import { arraysAreEqual } from "../thunks/report.functions";

export enum SelectedZoneOrWorkplaceType {
  ZONE = "zone",
  WORKPLACE = "workplace"
}

export type SelectedZoneOrWorkplace = {
  id: number[];
  name: string[];
  open: boolean;
  type: SelectedZoneOrWorkplaceType;
  floorInventoryId: number;
  bookingType: BookingType | undefined;
};

export type ReportState = {
  isLoading: boolean;
  calculateWithoutCosts: boolean;
  compareTo: { type: string; ids: number[] };
  currentFloors: { id: number; open: boolean }[];
  selectedZoneOrWorkplace: SelectedZoneOrWorkplace;
  selectedByAttFilter: SelectedZoneOrWorkplace[];
  selectedLocation: { id: number; open: boolean; sub: string };
  mapMarkerLocations?: MapMarkerLocation[];
  capacitySelectedWorkplaceOrZone?: CapacityObject;
  capacitySelectedByAttFilter?: CapacityObject[];
  capacitySelectedLocations?: CapacityObject[];
  capacitySelectedFloors?: CapacityObject[];
  selectableEntities?: ReportEntities[];
  initialTimeframe?: TimeframeString;
  selectedTimeframe?: TimeframeString;
  zoneRestrictions?: RestrictionZone;
  equipment: EquipmentFilter;
  filterOptions: ReportFilters;
  filterSelected: ReportFilters;
  selectedBookingType: BookingType | null;
  isSelected: boolean;
};

const initialState: ReportState = {
  isLoading: false,
  calculateWithoutCosts: true,
  selectedLocation: { id: -1, open: false },
  currentFloors: [{ id: -1, open: false }],
  zoneRestrictions: {
    company: { id: 0, name: "" },
    object: { id: 0, zoneBookingObject: "", type: "", name: "" }
  },
  equipment: {},
  filterOptions: {},
  selectedBookingType: BookingType.WORKPLACE,
  filterSelected: {
    selectedWeekdays: {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false
    },
    selectedDepartmentIds: [],
    selectedAttributes: []
  },
  isSelected: false
} as unknown as ReportState;

const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    setIsSelected(state, action: PayloadAction<ReportState["isSelected"]>) {
      state.isSelected = action.payload;
    },
    setMarkers(state, action: PayloadAction<ReportState["mapMarkerLocations"]>) {
      state.mapMarkerLocations = action.payload;
    },
    setCompareTo(state, action: PayloadAction<ReportState["compareTo"]>) {
      state.compareTo = action.payload;
    },
    setInitialTimeframe(state, action: PayloadAction<ReportState["initialTimeframe"]>) {
      state.initialTimeframe = action.payload;
    },
    setCalculateWithoutCosts(state, action: PayloadAction<ReportState["calculateWithoutCosts"]>) {
      state.calculateWithoutCosts = action.payload;
    },
    setSelectedTimeframe(state, action: PayloadAction<ReportState["selectedTimeframe"]>) {
      state.selectedTimeframe = action.payload;
    },
    setLoading(state, action: PayloadAction<ReportState["isLoading"]>) {
      state.isLoading = action.payload;
    },
    setCurrentFloors(state, action: PayloadAction<ReportState["currentFloors"]>) {
      state.currentFloors = action.payload;
    },
    setSelectedLocation(state, action: PayloadAction<ReportState["selectedLocation"]>) {
      state.selectedLocation = action.payload;
    },
    setZoneRestrictions(
      state,
      action: PayloadAction<{
        object?: UserZoneBookingObject;
        company?: Company;
      }>
    ) {
      if (action.payload?.object && action.payload?.company) {
        state.zoneRestrictions = action.payload as RestrictionZone;
      }
      if (action.payload?.object && state.zoneRestrictions?.object) {
        state.zoneRestrictions.object = action.payload?.object;
      }
      if (action.payload?.company && state.zoneRestrictions?.company) {
        state.zoneRestrictions.company = action.payload?.company;
      }
    },
    setSelectedZoneOrWorkplace(
      state,
      action: PayloadAction<ReportState["selectedZoneOrWorkplace"]>
    ) {
      state.selectedZoneOrWorkplace = action.payload;
    },
    setSelectedByAttFilter(state, action: PayloadAction<ReportState["selectedZoneOrWorkplace"]>) {
      // handle the empty sign
      if (
        !action.payload.id.length &&
        !action.payload.open &&
        action.payload.floorInventoryId === -1
      ) {
        state.selectedByAttFilter = [];
        return;
      }

      if (state.selectedByAttFilter) {
        const alreadyFetched = state.selectedByAttFilter.some(sel =>
          arraysAreEqual(sel.id, action.payload.id)
        );
        if (alreadyFetched) {
          const i = state.selectedByAttFilter.findIndex(sel =>
            arraysAreEqual(sel.id, action.payload.id)
          );
          state.selectedByAttFilter[i] = action.payload;
        } else {
          state.selectedByAttFilter = [...state.selectedByAttFilter, action.payload];
        }
      } else state.selectedByAttFilter = [action.payload];
    },
    setCapacitySelectedWorkplaceOrZone(
      state,
      action: PayloadAction<ReportState["capacitySelectedWorkplaceOrZone"]>
    ) {
      state.capacitySelectedWorkplaceOrZone = action.payload;
    },
    setCapacitySelectedByAttFilter(state, action: PayloadAction<CapacityObject>) {
      if (state.capacitySelectedByAttFilter) {
        const alreadyFetched = state.capacitySelectedByAttFilter.some(
          sel => sel.id === action.payload.id
        );
        if (alreadyFetched) {
          const i = state.capacitySelectedByAttFilter.findIndex(
            sel => sel.id === action.payload.id
          );
          state.capacitySelectedByAttFilter[i].capacity = action.payload.capacity;
        } else {
          state.capacitySelectedByAttFilter = [
            ...state.capacitySelectedByAttFilter,
            action.payload
          ];
        }
      } else state.capacitySelectedByAttFilter = [action.payload];
    },
    setCapacitySelectedLocations(state, action: PayloadAction<CapacityObject>) {
      if (state.capacitySelectedLocations) {
        const alreadyFetched = state.capacitySelectedLocations.some(
          location => location.id === action.payload.id
        );
        if (alreadyFetched) {
          state.capacitySelectedLocations = [
            ...state.capacitySelectedLocations.filter(floor => floor.id !== action.payload.id),
            action.payload
          ];
        } else {
          state.capacitySelectedLocations = [...state.capacitySelectedLocations, action.payload];
        }
      } else state.capacitySelectedLocations = [action.payload];
    },
    setCapacityDataEmpty(state, action: PayloadAction<boolean>) {
      if (action.payload) {
        state.capacitySelectedByAttFilter = [];
        state.capacitySelectedFloors = [];
        state.capacitySelectedWorkplaceOrZone = undefined;
        state.capacitySelectedLocations = [];
      }
    },
    setCapacitySelectedFloors(state, action: PayloadAction<CapacityObject>) {
      /** refactored to update the capacity selected floors */
      if (state.capacitySelectedFloors) {
        const alreadyFetched = state.capacitySelectedFloors.some(
          floor => floor.id === action.payload.id
        );
        if (alreadyFetched) {
          const i = state.capacitySelectedFloors.findIndex(flo => flo.id === action.payload.id);
          state.capacitySelectedFloors[i].capacity = action.payload.capacity;
        } else {
          state.capacitySelectedFloors = [...state.capacitySelectedFloors, action.payload];
        }
      } else state.capacitySelectedFloors = [action.payload];
    },
    setSelectableEntities(state, action: PayloadAction<ReportEntities>) {
      if (state.selectableEntities) {
        state.selectableEntities = [...state.selectableEntities, action.payload];
      } else {
        state.selectableEntities = [action.payload];
      }
    },
    /** Replaces selectableEntities entirely (e.g. for fleet capacity report). */
    setSelectableEntitiesReplace(state, action: PayloadAction<ReportEntities[]>) {
      state.selectableEntities = action.payload;
    },
    setEquipment(state, action: PayloadAction<EquipmentFilter>) {
      const { equipmentIds, equipmentCategoryIds, workplaceCategoryIds } = action.payload;

      if (equipmentIds) state.equipment.equipmentIds = equipmentIds;
      if (equipmentCategoryIds) state.equipment.equipmentCategoryIds = equipmentCategoryIds;
      if (workplaceCategoryIds) state.equipment.workplaceCategoryIds = workplaceCategoryIds;
    },
    setFilterOptions(state, action: PayloadAction<ReportState["filterOptions"]>) {
      const { equipments, equipmentCategories, placeCategories } = action.payload;
      if (equipments) state.filterOptions.equipments = equipments;
      if (equipmentCategories) state.filterOptions.equipmentCategories = equipmentCategories;
      if (placeCategories) state.filterOptions.placeCategories = placeCategories;

      // Reset selected when fetching new options
      state.filterSelected = {};
    },
    setFilterSelected(state, action: PayloadAction<ReportState["filterSelected"]>) {
      const {
        equipments,
        equipmentCategories,
        placeCategories,
        selectedDepartmentIds,
        selectedWeekdays,
        selectedAttributes
      } = action.payload;

      if (equipments) state.filterSelected.equipments = equipments;
      if (equipmentCategories) state.filterSelected.equipmentCategories = equipmentCategories;
      if (placeCategories) state.filterSelected.placeCategories = placeCategories;
      if (selectedDepartmentIds) state.filterSelected.selectedDepartmentIds = selectedDepartmentIds;
      if (selectedWeekdays) state.filterSelected.selectedWeekdays = selectedWeekdays;
      if (selectedAttributes) state.filterSelected.selectedAttributes = selectedAttributes;
    },
    setSelectedBookingType(state, action: PayloadAction<ReportState["selectedBookingType"]>) {
      state.selectedBookingType = action.payload;
    }
  }
});

export default reportSlice.reducer;
export const {
  setMarkers,
  setSelectableEntities,
  setSelectableEntitiesReplace,
  setInitialTimeframe,
  setSelectedTimeframe,
  setCapacitySelectedLocations,
  setCapacitySelectedFloors,
  setSelectedLocation,
  setCompareTo,
  setLoading,
  setCurrentFloors,
  setSelectedZoneOrWorkplace,
  setSelectedByAttFilter,
  setCapacitySelectedWorkplaceOrZone,
  setCapacitySelectedByAttFilter,
  setCalculateWithoutCosts,
  setZoneRestrictions,
  setEquipment,
  setFilterOptions,
  setFilterSelected,
  setSelectedBookingType,
  setIsSelected,
  setCapacityDataEmpty
} = reportSlice.actions;
